/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  KeyboardEventHandler,
} from 'react';
import ReactSelect, {
  OptionTypeBase,
  Props as SelectProps,
  ValueType,
  components,
} from 'react-select';
import { useField } from '@unform/core';
import { useTheme } from 'styled-components';

import Icon from 'components/Tools/Icon';
import selectArrowIcon from 'assets/svg/selectArrowIcon.svg';
import { Container } from './styles';

interface inputErrorProps {
  inputName: string;
  message: string;
}

interface Props extends SelectProps<OptionTypeBase> {
  label?: string;
  name: string;
  multiSelect?: boolean | undefined;
  hasChanged?(value: number | null): void;
  handleInputError?(data: inputErrorProps): void;
  messageErrorOnBlur?: string;
  onChangeEvent?(value: any): void;
  isApiSearch?: boolean;
  defaultValue?: ValueType<OptionTypeBase, false>;
  handleInputChange?: (newValue: string, actionMeta: string) => void;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
}

const InputSearch: React.FC<Props> = ({
  name,
  handleInputError,
  onChangeEvent,
  messageErrorOnBlur,
  multiSelect = false,
  isApiSearch = false,
  label,
  handleInputChange,
  options = [],
  onKeyDown,
  ...rest
}) => {
  const { title } = useTheme();

  const themedDarkColor = title === 'dark' ? '#4C4C66' : '#B5B5C3';

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [closeOnSelect, setCloseOnSelect] = useState<boolean>(false);
  const [focusedOption, setFocusedOption] = useState<OptionTypeBase | null>(
    null
  );

  const selectRef = useRef<any>(null);
  const { fieldName, registerField, error, clearError } = useField(name);

  const hasOptions = Array.isArray(options) && options.length > 0;

  const SelectIcon = () =>
    !isApiSearch ? (
      <Icon
        icon={selectArrowIcon}
        color={isFocused ? '#0095E8' : themedDarkColor}
      />
    ) : null;

  const closeSelect = useCallback(() => {
    setCloseOnSelect(true);
    setIsFocused(false);
    setFocusedOption(null);

    if (selectRef.current?.blur) {
      selectRef.current.blur();
    } else if (selectRef.current?.select?.blur) {
      selectRef.current.select.blur();
    }
  }, []);

  const handleSelectOption = useCallback(
    (option: any) => {
      if (!option) return;

      clearError();

      if (onChangeEvent) {
        onChangeEvent(option);
      }

      closeSelect();
    },
    [clearError, onChangeEvent, closeSelect]
  );

  const handleBlur = useCallback(
    (event: any) => {
      setIsFocused(false);
      setCloseOnSelect(false);
      setFocusedOption(null);

      if (!inputValue && !event?.target?.value) {
        if (handleInputError && messageErrorOnBlur) {
          handleInputError({
            inputName: name,
            message: messageErrorOnBlur,
          });
        }
      }
    },
    [handleInputError, messageErrorOnBlur, name, inputValue]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = event => {
    if (event.key === 'Enter' && isApiSearch && isFocused && hasOptions) {
      event.preventDefault();
      event.stopPropagation();

      /**
       * Aqui está a correção:
       * seleciona a opção destacada/focada.
       * Se por algum motivo não houver opção focada, aí sim cai na primeira.
       */
      const optionToSelect =
        focusedOption || (Array.isArray(options) ? options[0] : null);

      handleSelectOption(optionToSelect);

      return;
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const CustomOption = (props: any) => {
    const { data, isFocused: optionIsFocused, innerProps } = props;

    useEffect(() => {
      if (optionIsFocused) {
        setFocusedOption(data);
      }
    }, [optionIsFocused, data]);

    return (
      // @ts-ignore
      <components.Option
        {...props}
        innerProps={{
          ...innerProps,
          onMouseMove: (event: any) => {
            setFocusedOption(data);

            if (innerProps?.onMouseMove) {
              innerProps.onMouseMove(event);
            }
          },
          onMouseEnter: (event: any) => {
            setFocusedOption(data);

            if (innerProps?.onMouseEnter) {
              innerProps.onMouseEnter(event);
            }
          },
        }}
      />
    );
  };

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: { value: inputValue },
      getValue: () => inputValue,
      setValue: (_ref: any, value: string) => {
        setInputValue(value || '');
        setCloseOnSelect(false);
        setFocusedOption(null);
        clearError();
      },
      clearValue: () => {
        setInputValue('');
        setCloseOnSelect(false);
        setFocusedOption(null);
      },
    });
  }, [fieldName, registerField, clearError, inputValue]);

  return (
    <Container error={!!error} hasLabel={!!label}>
      <label className="label" htmlFor={`${name}Id`}>
        {label}
      </label>

      {/* @ts-ignore */}
      <ReactSelect
        // @ts-ignore
        isMulti={multiSelect}
        ref={selectRef}
        value={null}
        inputValue={inputValue}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        classNamePrefix="sel"
        onBlur={handleBlur}
        id={`${name}Id`}
        isClearable={false}
        options={options}
        onKeyDown={handleKeyDown}
        filterOption={() => true}
        components={{
          DropdownIndicator: SelectIcon,
          Option: CustomOption,
        }}
        controlShouldRenderValue={false}
        backspaceRemovesValue={false}
        onInputChange={(query, { action }) => {
          if (action === 'input-change') {
            setInputValue(query);
            setCloseOnSelect(false);
            setFocusedOption(null);
            clearError();

            if (handleInputChange) {
              handleInputChange(query, action);
            }

            return query;
          }

          if (action === 'set-value') {
            return inputValue;
          }

          return inputValue;
        }}
        onFocus={() => {
          clearError();
          setIsFocused(true);
          setCloseOnSelect(false);
          setFocusedOption(null);
        }}
        onChange={(event: ValueType<OptionTypeBase, false>) => {
          handleSelectOption(event);
        }}
        menuIsOpen={isFocused && hasOptions && !closeOnSelect}
        {...rest}
      />
    </Container>
  );
};

export default InputSearch;
